import { useStepperData } from "../../Helpers/Contexts/stepperContext";
import ReusableStepper from "../../Components/ReusableStepper";
import EducationalBackground from "./components/EducationalBackground";
import Experience from "./components/Experience";
import JourneyWithMCA from "./components/JourneyWithMCA";
import NCACertified from "./components/NCACertified";
import { useToast } from "../../Helpers/Contexts/toastContext";
import { useLocation, useNavigate } from "react-router-dom";
import routes from "../../Utils/routes";
import CertificateInstitution from "./components/CertificateInstitution";
import CoachCertificate from "./components/CoachCertificate";
import {
  postStaffRegestration,
  updateStaffRegDetails,
} from "../../Utils/apiService";
import { useState } from "react";
import KenLoader from "../../GlobalComponents/KenLoader";

type RoleType =
  | "Physio"
  | "S&C Coach"
  | "Analyst"
  | "Masseur"
  | "Coach & Assistant Coach";

const StaffRegistration = () => {
  const { formData, resetFormData } = useStepperData();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const { title, expertiseId } = location.state || {};
  const [loading, setLoading] = useState(false);

  const role = title as RoleType;

  const validateSharedEducation = () => {
    if (!formData.Highest_Qualification__c) {
      showToast("error", "Please select your highest qualification");
      return false;
    }
    if (
      formData.Highest_Qualification__c === "Other" &&
      !formData.Highest_Qualification_Other__c?.trim()
    ) {
      showToast("error", "Please enter your qualification");
      return false;
    }
    if (!formData.Highest_Qualification_Certification_URL__c) {
      showToast("error", "Please upload your qualification certificate");
      return false;
    }
    if (!formData.Institute_Name__c?.trim()) {
      showToast("error", "Please enter your institution name");
      return false;
    }
    return true;
  };

  const stepFieldMap: Record<number, string[]> = {
    0: [
      "Highest_Qualification__c",
      "Highest_Qualification_Other__c",
      "Highest_Qualification_Certification_URL__c",
      "Institute_Name__c",
      "Masseur_Certification__c",
      "Masseur_Certification_Other__c",
      "Masseur_Certification_URL__c",
      "Certified_Coach__c",
      "Coach_Certification_URL__c",
    ],
    1: ["Total_Years_of_Experience_In_Years__c"],
    2: ["Experience_at_MCA_In_Years__c"],
    3: ["NCA_Certified__c", "NCA_Certification_Details__c"],
  };

  const firstStepMap: Record<
    RoleType,
    {
      label: string;
      content: JSX.Element;
      validate: (formData: any, showToast: any) => boolean;
    }
  > = {
    Physio: {
      label: "Educational Background",
      content: <EducationalBackground />,
      validate: validateSharedEducation,
    },
    "S&C Coach": {
      label: "Educational Background",
      content: <EducationalBackground />,
      validate: validateSharedEducation,
    },
    Analyst: {
      label: "Educational Background",
      content: <EducationalBackground />,
      validate: validateSharedEducation,
    },
    Masseur: {
      label: "Educational Background",
      content: <CertificateInstitution />,
      validate: () => {
        if (!formData.Masseur_Certification__c?.trim()) {
          showToast("error", "Please enter your degree or qualification");
          return false;
        }
        return true;
      },
    },
    "Coach & Assistant Coach": {
      label: "Coaching Level & Education",
      content: <CoachCertificate />,
      validate: () => {
        if (!formData.Certified_Coach__c) {
          showToast("error", "Please Select");
          return false;
        } else if (
          formData.Certified_Coach__c == "Yes" &&
          !formData.Coach_Certification_URL__c
        ) {
          showToast("error", "Please upload your qualification certificate");
          return false;
        }
        return true;
      },
    },
  };

  const firstStep = firstStepMap[role];

  const steps = [
    {
      label: firstStep.label,
      content: firstStep.content,
      validate: () => firstStep.validate(formData, showToast),
    },
    {
      label: "Work Experience",
      content: <Experience />,
      validate: () => {
        if (!formData.Total_Years_of_Experience_In_Years__c?.trim()) {
          showToast("error", "Please enter your experience");
          return false;
        }
        return true;
      },
    },
    {
      label: "Journey with MCA",
      content: <JourneyWithMCA />,
      validate: () => {
        if (!formData.Experience_at_MCA_In_Years__c?.trim()) {
          showToast("error", "Please enter your experience in MCA");
          return false;
        }
        return true;
      },
    },
    {
      label: "NCA Certification",
      content: <NCACertified />,
      validate: () => {
        if (!formData.NCA_Certified__c) {
          showToast("error", "Please select whether you are NCA certified");
          return false;
        }
        if (
          formData.NCA_Certified__c === "Yes" &&
          !formData.NCA_Certification_Details__c?.trim()
        ) {
          showToast("error", "Please enter NCA certificate details");
          return false;
        }
        return true;
      },
    },
  ];

  const handleSubmit = async (data: Record<string, any>) => {
    console.log("🚀 Submitted Payload:", data);
    setLoading(true);
    const staffRegId = localStorage.getItem("staffRegisterNumber");
    const payload: Record<string, any> = {
      Application_Status__c: "Completed",
      NCA_Certified__c: data.NCA_Certified__c,
      ...(data.NCA_Certified__c === "Yes" && {
        NCA_Certification_Details__c: data.NCA_Certification_Details__c,
      }),
    };

    console.log("📦 Final Submission Payload:", payload);
    try {
      await updateStaffRegDetails(staffRegId, payload)
        .then((res) => {
          resetFormData();
          localStorage.removeItem("staffRegisterNumber");
          navigate(routes.thankYou);
          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    } catch (err) {
      console.error("❌ Submit failed:", err);
    }
  };

  const handleStepUpdate = async (data: Record<string, any>, step: number) => {
    console.log(`📤 Step ${step + 1} update payload:`, data);
    const contactId: any = localStorage.getItem("contact-id");
    const staffRegId = localStorage.getItem("staffRegisterNumber");

    const stepFields = stepFieldMap[step] || [];

    const stepData: Record<string, any> = {};
    stepFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        stepData[field] = data[field];
      }
    });

    if (step === 0) {
      stepData.Contact__c = contactId;
      stepData.Application_Form__c = expertiseId;
      stepData.Application_Status__c = "Incomplete";
    }

    if (staffRegId) {
      setLoading(true);
      // if (hasChanges(updateData, originalPlayerReg)) {
      await updateStaffRegDetails(staffRegId, stepData)
        .then((res) => {
          console.log(res, "updateStaffRegDetails");
          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });

      // setOriginalPlayerReg((prev: any) => ({ ...prev, ...updateData }));
      // }
    } else {
      setLoading(true);
      const response = await postStaffRegestration(stepData);
      console.log(response, "postStaffRegestration");
      localStorage.setItem("staffRegisterNumber", response.data.data.id);
      // setOriginalPlayerReg(updateData);
    }
    // await update API per step here
  };

  return (
    <>
      <ReusableStepper
        loading={loading}
        setLoading={setLoading}
        steps={steps}
        onFinalSubmit={handleSubmit}
        onStepUpdate={handleStepUpdate}
      />
    </>
  );
};

export default StaffRegistration;
